import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Compass, Telescope, CheckCircle2 } from "lucide-react";
import CosmicBackground from "./CosmicBackground";

const MissionVision = () => {
  const { t } = useTranslation();
  const visionPoints = t("mission.vision_points", { returnObjects: true });

  return (
    <section
      data-testid="mission-section"
      className="relative section-pad overflow-hidden"
    >
      <CosmicBackground variant="football" />

      <div className="relative mx-auto max-w-[1300px] px-5 md:px-10">
        <div className="text-center">
          <span className="eyebrow">{t("mission.eyebrow")}</span>
        </div>

        <div className="mt-14 grid gap-7 lg:grid-cols-2">
          {/* Mission */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6 }}
            className="glass-strong relative overflow-hidden rounded-3xl p-9 lg:p-12 electric-border"
          >
            <div
              aria-hidden
              className="absolute -right-20 -top-20 h-72 w-72 rounded-full opacity-50 blur-3xl"
              style={{ background: "#FF2D5C" }}
            />
            <div className="relative">
              <div className="flex items-center gap-3">
                <Compass className="h-6 w-6 text-[#FF2D5C]" />
                <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#FF2D5C]">
                  {t("mission.mission_title")}
                </span>
              </div>
              <h3 className="font-display mt-5 text-xl md:text-[1.75rem] font-semibold leading-[1.18] text-white">
                <span className="glow-text-red">Mission.</span>
              </h3>
              <p className="mt-6 text-base md:text-lg text-white/80 leading-relaxed">
                {t("mission.mission_body")}
              </p>
            </div>
          </motion.div>

          {/* Vision */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="glass-strong relative overflow-hidden rounded-3xl p-9 lg:p-12 electric-border"
          >
            <div
              aria-hidden
              className="absolute -left-20 -bottom-20 h-72 w-72 rounded-full opacity-50 blur-3xl"
              style={{ background: "#3DBFFF" }}
            />
            <div className="relative">
              <div className="flex items-center gap-3">
                <Telescope className="h-6 w-6 text-[#3DBFFF]" />
                <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#3DBFFF]">
                  {t("mission.vision_title")}
                </span>
              </div>
              <h3 className="font-display mt-5 text-xl md:text-[1.75rem] font-semibold leading-[1.18] text-white">
                <span className="glow-text-blue">Vision.</span>
              </h3>
              <p className="mt-6 text-base md:text-lg text-white/80 leading-relaxed">
                {t("mission.vision_body")}
              </p>
              <ul className="mt-5 space-y-3">
                {Array.isArray(visionPoints) &&
                  visionPoints.map((p, i) => (
                    <li
                      key={i}
                      data-testid={`mission-vision-point-${i}`}
                      className="flex items-start gap-3 text-sm md:text-base text-white/80"
                    >
                      <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#3DBFFF]" />
                      <span>{p}</span>
                    </li>
                  ))}
              </ul>
            </div>
          </motion.div>
        </div>

        {/* Long-term goal */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="mt-7 rounded-3xl p-9 md:p-12 text-center electric-border"
          style={{
            background:
              "linear-gradient(135deg, rgba(255,45,92,0.08) 0%, rgba(155,77,255,0.10) 50%, rgba(61,191,255,0.08) 100%)",
            backdropFilter: "blur(20px)",
          }}
        >
          <span className="eyebrow">{t("mission.goal_title")}</span>
          <p className="font-display mx-auto mt-5 max-w-3xl text-xl md:text-2xl leading-snug text-white">
            <span className="headline-neon">{t("mission.goal_body")}</span>
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default MissionVision;
